import { skipToken } from "@reduxjs/toolkit/query";
import type { SignalMatch } from "@repo/app-types/entities/signalMatch";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";

import { createContext, type FC, useContext } from "react";
import { useParams } from "react-router";
import { usePlaybookGradingQuery } from "../../../api/entities/performancePlaybook.js";
import { SignalMatchSlideoverDialog } from "./signalMatchSlideoverDialog.js";
import type { MetadataFieldLabel } from "./signalRecentMatches.js";

/**
 * Context for passing match data + metadata field labels from the drilldown
 * page to the slideover route.
 */
interface SignalMatchSlideoverContextValue {
  matches: readonly SignalMatch[];
  metadataFields: readonly MetadataFieldLabel[];
  signalName: string;
}

const SignalMatchSlideoverContext =
  createContext<SignalMatchSlideoverContextValue>({
    matches: [],
    metadataFields: [],
    signalName: "",
  });

export const SignalMatchSlideoverProvider =
  SignalMatchSlideoverContext.Provider;

/**
 * Route-level container for the signal match slideover.
 * Extracts matchId from URL params, looks up the match from context,
 * fetches transcript recordings, and renders the slideover dialog.
 */
export const SignalMatchSlideoverContainer: FC = () => {
  const { matchId } = useParams();
  const { matches, metadataFields, signalName } = useContext(
    SignalMatchSlideoverContext,
  );

  const match = matches.find((m) => m.matchId === matchId);

  // Fetch grading (which includes recordings with transcripts) for the match
  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery(match ? { gradingId: match.gradingId } : skipToken);

  if (!match || !grading) {
    return (
      <SlideoverDialog label="Signal match">
        <SlideoverDialogTitleHeader isLoading={isGradingLoading || !match}>
          Signal match
        </SlideoverDialogTitleHeader>
        <SlideoverDialogContentGray>
          {!isGradingLoading && !match ? (
            <div className="p-4 text-sm text-muted-foreground">
              Match not found.
            </div>
          ) : null}
        </SlideoverDialogContentGray>
      </SlideoverDialog>
    );
  }

  return (
    <SignalMatchSlideoverDialog
      match={match}
      fields={metadataFields}
      recordings={grading.recordings}
      signalName={signalName}
    />
  );
};
